import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { SITTER_PROFILE_EVENTSTREAM_EVENTS } from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box } from '@rover/kibble/core';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { ViewerOptions } from '../../constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';

import { SitterProfileServiceChanged, SitterProfileServiceSelectorOpened } from './analyticsEvents';
import RevampMobileCtaBarOwnerView from './RevampMobileCtaBarOwnerView';
import RevampMobileCtaBarSelfView from './RevampMobileCtaBarSelfView';
import { CTAProps } from './RevampServicesCta';
import RevampServiceSelector from './RevampServiceSelector';

const MOBILE_TABLET_DISPLAY = ['block', 'block', 'none'];

const RevampMobileCtaBar = ({
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
  bookingFeePercentage,
  isApproved,
}: CTAProps): JSX.Element | null => {
  const { sitterData, serviceType, setServiceType, viewMode } = useRevampProfileContext();
  const isSelfView = viewMode.viewer === ViewerOptions.SELF;

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isAtRest, setIsAtRest] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!process.env.JS_ENV_CLIENT) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAtRest(entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleOpenSheet = useCallback(() => {
    setIsSheetOpen(true);
    emitAnalyticsEvent(new SitterProfileServiceSelectorOpened({ providerId: sitterData.opk }));
  }, [sitterData.opk]);

  const handleCloseSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const handleServiceSave = useCallback(
    (slug: string) => {
      if (slug !== serviceType) {
        setServiceType(slug);
        fireDataLayerEvent({
          event: SITTER_PROFILE_EVENTSTREAM_EVENTS.CHANGE_SERVICE,
          serviceType: slug,
          providerId: sitterData.opk,
        });
        emitMetric('sitter_profile_service_changed', { component: 'revamp-mobile-cta' });
        emitAnalyticsEvent(
          new SitterProfileServiceChanged({
            serviceSlug: slug,
            providerId: sitterData.opk,
            pageVariant: 'revamp',
          })
        );
      }
    },
    [serviceType, setServiceType, sitterData.opk]
  );

  if (sitterData.services.length === 0) return null;

  return (
    <>
      <Box ref={sentinelRef} height="0px" display={MOBILE_TABLET_DISPLAY} />
      <Box
        data-testid="revamp-mobile-cta-bar"
        position="sticky"
        bottom="0"
        display={MOBILE_TABLET_DISPLAY}
        background="primary"
        p="4x"
        borderTopLeftRadius={isAtRest ? '0px' : '16px'}
        borderTopRightRadius={isAtRest ? '0px' : '16px'}
        sx={{
          zIndex: ZIndex.STICKY_HEADER.toNumber(),
          boxShadow: '0px 2px 12px 0px rgba(27, 31, 35, 0.24)',
          transition: 'border-radius 0.2s ease',
        }}
      >
        {isSelfView ? (
          <RevampMobileCtaBarSelfView
            handleOpenSheet={handleOpenSheet}
            priceTransparencyLevel={priceTransparencyLevel}
            locale={locale}
            petCount={petCount}
            petType={petType}
            bookingFeePercentage={bookingFeePercentage}
            isApproved={isApproved}
          />
        ) : (
          <RevampMobileCtaBarOwnerView
            handleOpenSheet={handleOpenSheet}
            priceTransparencyLevel={priceTransparencyLevel}
            locale={locale}
            petCount={petCount}
            petType={petType}
            bookingFeePercentage={bookingFeePercentage}
          />
        )}
      </Box>

      <RevampServiceSelector
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        services={sitterData.services}
        selectedServiceSlug={serviceType}
        onSave={handleServiceSave}
        variant="mobile"
      />
    </>
  );
};

export default RevampMobileCtaBar;
